import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../store/auth';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../lib/api-hooks';
import { formatDate } from '../lib/format';

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deviceNavn, setDeviceNavn] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const { data: keys, isLoading } = useQuery({
    queryKey: ['my-keys'],
    queryFn: () => authApi.listMyKeys(),
  });

  const addKeyMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      // 1. Hent options fra backend
      const { options } = await authApi.addKeyOptions();
      // 2. Kjør WebAuthn i nettleser
      const credential = await startRegistration(options);
      // 3. Verifiser hos backend
      return authApi.verifyAddKey({ credential, deviceNavn: deviceNavn || undefined });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-keys'] });
      setShowAddForm(false);
      setDeviceNavn('');
      setSuccessMsg('Sikkerhetsnøkkel lagt til.');
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      const msg =
        err?.name === 'NotAllowedError'
          ? 'Ingen sikkerhetsnøkkel ble brukt eller operasjonen ble avbrutt.'
          : err?.message ?? 'Kunne ikke legge til sikkerhetsnøkkel.';
      setError(msg);
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: (credId: string) => authApi.deleteMyKey(credId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-keys'] });
      setSuccessMsg('Sikkerhetsnøkkel slettet.');
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      setError(err?.message ?? 'Kunne ikke slette nøkkel.');
      setTimeout(() => setError(null), 3000);
    },
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:px-6">
      <h1 className="mb-6 text-xl font-semibold text-slate-800">Profil</h1>

      {/* Brukerinfo */}
      <div className="card mb-6 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 text-xl font-semibold text-brand-700">
            {user?.navn?.charAt(0).toUpperCase() ?? '?'}
          </div>
          <div>
            <p className="font-medium text-slate-800">{user?.navn}</p>
            <p className="text-sm text-slate-500">{user?.epost}</p>
            <span className="chip mt-1 bg-brand-100 text-brand-700">
              {user?.rolle}
            </span>
          </div>
        </div>
      </div>

      {/* Sikkerhetsnøkler */}
      <div className="card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-slate-800">Sikkerhetsnøkler</h2>
            <p className="text-sm text-slate-500">
              Registrer én nøkkel per enhet — f.eks. én for PC og én for mobil.
            </p>
          </div>
          {!showAddForm && (
            <button
              onClick={() => { setShowAddForm(true); setError(null); }}
              className="btn-primary text-sm"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              Legg til nøkkel
            </button>
          )}
        </div>

        {/* Suksess/feilmelding */}
        {successMsg && (
          <div className="mb-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Legg til nøkkel-form */}
        {showAddForm && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Navn på enhet (valgfritt)
            </label>
            <input
              type="text"
              value={deviceNavn}
              onChange={(e) => setDeviceNavn(e.target.value)}
              placeholder="f.eks. «iPhone» eller «Arbeids-PC»"
              className="input mb-3"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => addKeyMutation.mutate()}
                disabled={addKeyMutation.isPending}
                className="btn-primary text-sm"
              >
                {addKeyMutation.isPending ? 'Registrerer…' : 'Registrer nøkkel'}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setDeviceNavn(''); setError(null); }}
                className="btn-secondary text-sm"
              >
                Avbryt
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Du vil bli bedt om å bruke biometri eller en sikkerhetsnøkkel i nettleseren.
            </p>
          </div>
        )}

        {/* Liste over nøkler */}
        {isLoading ? (
          <p className="text-sm text-slate-400">Laster…</p>
        ) : keys && keys.length > 0 ? (
          <div className="space-y-2">
            {keys.map((cred) => (
              <div
                key={cred.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12l7-7 7 7-7 7-7-7z" />
                      <path d="M14 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {cred.deviceNavn || 'Navnløs nøkkel'}
                    </p>
                    <p className="text-xs text-slate-400">
                      Lagt til {formatDate(cred.opprettet)}
                      {cred.sistBrukt && ` · Sist brukt ${formatDate(cred.sistBrukt)}`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Slette «${cred.deviceNavn || 'denne nøkkelen'}»? Du kan ikke logge inn med den etterpå.`)) {
                      deleteKeyMutation.mutate(cred.id);
                    }
                  }}
                  disabled={deleteKeyMutation.isPending}
                  className="text-slate-400 transition-colors hover:text-red-600"
                  title="Slett nøkkel"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Ingen sikkerhetsnøkler registrert enda.
          </p>
        )}

        {/* Logout */}
        <div className="mt-6 border-t border-slate-200 pt-4">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="btn-danger"
          >
            Logg ut
          </button>
        </div>
      </div>
    </div>
  );
}
